import React from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import { CircularProgress, Divider, IconButton, Menu, MenuItem, Stack, Typography } from "@mui/material";
import logo_black from '../../assets/images/logo_black.png';
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import InstallMobileRoundedIcon from "@mui/icons-material/InstallMobileRounded";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "../../api/auth";
import { useSoftwareDownload } from "../../api/software";
import { useLocation } from "react-router";
import { userHasAppFunction, type AppFunctionKey } from "../../shared/types";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

type NavFeature = {
    label: string;
    needsAttention: boolean;
    url: string;
    /** `null` = always shown when logged in (e.g. Dashboard). */
    functionKey: AppFunctionKey | null;
};

const NAV_FEATURE_DEFINITIONS: NavFeature[] = [
    { label: "Dashboard", needsAttention: false, url: "/", functionKey: null },
    { label: "Goods In", needsAttention: false, url: "/goods-in", functionKey: "goods_in" },
    { label: "Stock", needsAttention: false, url: "/stock", functionKey: "stock" },
    { label: "Products", needsAttention: false, url: "/products", functionKey: "stock" },
    { label: "Stock locations", needsAttention: false, url: "/locations", functionKey: "stock_locations" },
    { label: "Relocations", needsAttention: false, url: "/relocations", functionKey: "assign_stock" },
    { label: "Non-conformance", needsAttention: false, url: "/non-conformance", functionKey: "non_conformance" },
    { label: "Users", needsAttention: false, url: "/users", functionKey: "users" },
    { label: "Sync issues", needsAttention: false, url: "/sync-issues", functionKey: "sync_issues" },
];


export default function NavigationHeader() {
    const [profileMenuAnchor, setProfileMenuAnchor] = React.useState<null | HTMLElement>(null);
    const [logoutOpen, setLogoutOpen] = React.useState(false);
    const [installOpen, setInstallOpen] = React.useState(false);
    const { logout, user } = useAuth();
    const location = useLocation();
    const { softwareDownload, error: installError, loading: installLoading } =
        useSoftwareDownload(installOpen);

    const handleOpenProfileMenu = (event: React.MouseEvent<HTMLElement>) => {
        setProfileMenuAnchor(event.currentTarget);
    };

    const handleCloseProfileMenu = () => {
        setProfileMenuAnchor(null);
    };

    const handleOpenLogout = () => {
        handleCloseProfileMenu();
        setLogoutOpen(true);
    };

    const handleOpenInstall = () => {
        handleCloseProfileMenu();
        setInstallOpen(true);
    };

    const handleCloseInstall = () => {
        setInstallOpen(false);
    };

    const handleCloseLogout = () => {
        setLogoutOpen(false);
    };

    const handleConfirmLogout = () => {
        logout();
        setLogoutOpen(false);
    };

    const initials = React.useMemo(() => {
        if (!user?.name) {
            return "?";
        }
        const parts = user.name.trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) {
            return "?";
        }
        if (parts.length === 1) {
            return parts[0].slice(0, 2).toUpperCase();
        }
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }, [user?.name]);

    const featureButtons = React.useMemo(
        () => NAV_FEATURE_DEFINITIONS.filter((f) => userHasAppFunction(user, f.functionKey)),
        [user]
    );

    return (
        <>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", flexDirection: "row", gap: 2, alignItems: "center" }}>
                    <img src={logo_black} alt="Carron Lodge Warehouse" height={48} />
                </Box>
                <Box>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {featureButtons.map((feature) => {
                            const isActive = feature.url === "/"
                                ? location.pathname === "/"
                                : location.pathname.startsWith(feature.url);
                            return (
                                <Button
                                    key={feature.url}
                                    variant={isActive ? "contained" : "text"}
                                    href={feature.url}
                                    color={feature.needsAttention ? "warning" : "primary"}
                                    endIcon={
                                        feature.needsAttention ? (
                                            <WarningAmberRoundedIcon
                                                fontSize="small"
                                            />
                                        ) : undefined
                                    }
                                >
                                    {feature.label}
                                </Button>
                            );
                        })}
                    </Stack>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "row", gap: 2, alignItems: "center" }}>
                    <IconButton onClick={handleOpenProfileMenu} size="small" aria-label="Open profile menu">
                        <Avatar sx={{ width: 42, height: 42, fontWeight: 600 }}>
                            {initials}
                        </Avatar>
                    </IconButton>
                    <Menu
                        anchorEl={profileMenuAnchor}
                        open={Boolean(profileMenuAnchor)}
                        onClose={handleCloseProfileMenu}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                        <MenuItem onClick={handleOpenInstall}>
                            <InstallMobileRoundedIcon fontSize="small" sx={{ mr: 1 }} />
                            Install app
                        </MenuItem>
                        <MenuItem onClick={handleOpenLogout}>
                            <LogoutRoundedIcon fontSize="small" sx={{ mr: 1 }} />
                            Log out
                        </MenuItem>

                    </Menu>
                </Box>
            </Box>
            <Divider />

            <Dialog open={installOpen} onClose={handleCloseInstall} maxWidth="xs" fullWidth>
                <DialogTitle>Install app</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} alignItems="center" sx={{ pt: 1 }}>
                        <Typography variant="body2" color="text.secondary" textAlign="center" component="div">
                            To install or update the warehouse app on your tablet:
                            <Box component="ol" sx={{ mt: 1, mb: 0, pl: 2.5, textAlign: "left" }}>
                                <li>Swipe down from the top of the screen</li>
                                <li>Tap <strong>QR code scanner</strong></li>
                                <li>Scan the code below</li>
                                <li>Tap <strong>Open</strong> to download the file</li>
                                <li>Tap <strong>Open</strong> or <strong>Install</strong> to install the app</li>
                            </Box>
                        </Typography>
                        {installLoading ? (
                            <CircularProgress size={40} />
                        ) : installError ? (
                            <Typography variant="body2" color="error" textAlign="center">
                                {installError}
                            </Typography>
                        ) : softwareDownload ? (
                            <>
                                <Box
                                    sx={{
                                        p: 2,
                                        bgcolor: "background.paper",
                                        borderRadius: 1,
                                        border: 1,
                                        borderColor: "divider",
                                    }}
                                >
                                    <QRCodeSVG value={softwareDownload.url} size={220} />
                                </Box>
                                <Typography variant="caption" color="text.secondary" textAlign="center">
                                    Version {softwareDownload.version} (build {softwareDownload.build})
                                </Typography>
                            </>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseInstall} variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={logoutOpen} onClose={handleCloseLogout} maxWidth="xs" fullWidth>
                <DialogTitle>Confirm logout</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to sign out? You will need to log in again to continue.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseLogout} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmLogout} color="error" variant="contained">
                        Log out
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};